<section
  class="rf-card"
  [attr.aria-busy]="state().loading || releasesState() === 'loading' || null"
  [attr.aria-label]="'testResults.freigabe.label' | translate"
>
  <div class="rf-head">
    <span class="rf-head__label">{{ 'testResults.freigabe.label' | translate }}</span>
    @if (releases().length > 0) {
      <select
        class="rf-head__select"
        #releaseSelect
        data-testid="release-select"
        [attr.aria-label]="'testResults.freigabe.releaseSelectLabel' | translate"
        (change)="onReleaseSelected(releaseSelect)"
      >
        @for (r of releases(); track r.id) {
          <option [value]="r.id" [selected]="r.id === releaseId()">{{ r.label }}</option>
        }
      </select>
    }
    @if (release(); as rel) {
      <span class="rf-head__meta">
        @if (rel.stichtag) {
          {{ 'testResults.freigabe.stichtag' | translate: { date: formatStichtag(rel.stichtag) } }} ·
        }
        {{ 'testResults.freigabe.status.' + rel.status | translate }}
        @if (summary(); as s) {
          ·
          {{
            (s.planCount === 1 ? 'testResults.freigabe.planCountOne' : 'testResults.freigabe.planCount')
              | translate: { count: s.planCount }
          }}
        }
      </span>
    }
    <span class="rf-head__spacer"></span>
    @if (ampel(); as a) {
      <app-ampel-badge data-testid="freigabe-ampel" [state]="a" />
    }
    <button
      type="button"
      class="rf-head__report"
      data-testid="report-open"
      [disabled]="!release()"
      (click)="reportOpen.set(true)"
    >
      <span aria-hidden="true">↓</span>
      {{ 'testResults.freigabe.report' | translate }}
    </button>
  </div>

  @if (!releaseId() && releasesState() === 'error') {
    <p class="rf-error" role="alert">{{ 'testResults.releasesLoadError' | translate }}</p>
  } @else if (!releaseId() && releasesState() === 'loaded') {
    <p class="rf-empty">{{ 'testResults.freigabe.noRelease' | translate }}</p>
  } @else if (state().error) {
    <p class="rf-error" role="alert">{{ 'testResults.freigabe.loadError' | translate }}</p>
  } @else if (summary(); as s) {
    <div class="rf-kpis">
      <div class="rf-kpi rf-kpi--progress" data-kpi="fortschritt">
        <div class="rf-kpi__label">{{ 'testResults.freigabe.kpi.progress' | translate }}</div>
        <div class="rf-kpi__row">
          <span class="rf-kpi__value">{{ s.fortschritt }}%</span>
          <span class="rf-kpi__inline">
            {{ 'testResults.freigabe.kpi.executedOf' | translate: { executed: s.ausgefuehrt, total: s.gesamt } }}
          </span>
        </div>
        <app-stack-bar
          class="rf-kpi__bar"
          [passed]="s.passed"
          [failed]="s.failed"
          [blocked]="s.blocked"
          [offen]="s.offen"
          [height]="10"
        />
        <div class="rf-legend">
          <span class="rf-legend__item">
            <span class="rf-legend__swatch rf-legend__swatch--passed" aria-hidden="true"></span>
            {{ 'testResults.freigabe.legend.passed' | translate }} {{ s.passed }}
          </span>
          <span class="rf-legend__item">
            <span class="rf-legend__swatch rf-legend__swatch--failed" aria-hidden="true"></span>
            {{ 'testResults.freigabe.legend.failed' | translate }} {{ s.failed }}
          </span>
          <span class="rf-legend__item">
            <span class="rf-legend__swatch rf-legend__swatch--blocked" aria-hidden="true"></span>
            {{ 'testResults.freigabe.legend.blocked' | translate }} {{ s.blocked }}
          </span>
          <span class="rf-legend__item">
            <span class="rf-legend__swatch rf-legend__swatch--open" aria-hidden="true"></span>
            {{ 'testResults.freigabe.legend.open' | translate }} {{ s.offen }}
          </span>
        </div>
      </div>

      <div class="rf-kpi" data-kpi="passrate">
        <div class="rf-kpi__label">{{ 'testResults.freigabe.kpi.passRate' | translate }}</div>
        <div class="rf-kpi__value" [attr.data-tone]="passRateTone(s.passRate)">{{ s.passRate }}%</div>
        <div class="rf-kpi__sub">{{ 'testResults.freigabe.kpi.passRateSub' | translate }}</div>
      </div>

      <div class="rf-kpi" data-kpi="blocker">
        <div class="rf-kpi__label">{{ 'testResults.freigabe.kpi.blockers' | translate }}</div>
        <div class="rf-kpi__value" [attr.data-tone]="blockerCount() > 0 ? 'bad' : 'good'">{{ blockerCount() }}</div>
        <div class="rf-kpi__sub">
          {{
            (blockerCount() > 0 ? 'testResults.freigabe.kpi.blockersSub' : 'testResults.freigabe.kpi.blockersNone')
              | translate
          }}
        </div>
      </div>

      <div class="rf-kpi" data-kpi="abdeckung">
        <div class="rf-kpi__label">{{ 'testResults.freigabe.kpi.coverage' | translate }}</div>
        <div
          class="rf-kpi__value"
          [attr.data-tone]="s.abdeckung.gesamt === 0 ? null : missingCoverage() === 0 ? 'good' : 'warn'"
        >
          {{ s.abdeckung.gedeckt }}/{{ s.abdeckung.gesamt }}
        </div>
        <div class="rf-kpi__sub">
          @if (s.abdeckung.gesamt === 0) {
            {{ 'testResults.freigabe.kpi.coverageNone' | translate }}
          } @else if (missingCoverage() === 0) {
            {{ 'testResults.freigabe.kpi.coverageAll' | translate }}
          } @else {
            {{ 'testResults.freigabe.kpi.coverageMissing' | translate: { count: missingCoverage() } }}
          }
        </div>
      </div>
    </div>

    @if (s.blocker.length > 0) {
      <div class="rf-blockers">
        <span class="rf-blockers__label">{{ 'testResults.freigabe.blockerStrip' | translate }}</span>
        @for (b of s.blocker; track b.planId + b.testCaseId) {
          <span class="rf-blocker" [attr.data-status]="b.status ?? 'NOT_RUN'">
            <code class="rf-blocker__id">{{ b.testCaseKey }}</code>
            <span class="rf-blocker__title">{{ b.titel }}</span>
            @if (b.status === null) {
              <span class="rf-blocker__state">{{ 'defect.ref.notRun' | translate }}</span>
            }
            @if (b.bugKey) {
              <code class="rf-blocker__bug">{{ b.bugKey }}</code>
            }
            <app-defect-ref-chips [projectId]="projectId()" [defects]="b.defects" />
            @if (b.prio) {
              <span class="rf-blocker__prio" [attr.data-prio]="b.prio">
                {{ 'testCases.priority.' + b.prio | translate }}
              </span>
            }
          </span>
        }
      </div>
    }
  }

  @if (release(); as rel) {
    <app-report-modal
      [projectId]="projectId()"
      [releaseId]="rel.id"
      [releaseLabel]="rel.label"
      [(visible)]="reportOpen"
    />
  }

  @if (releaseId(); as rid) {
    <app-freigabe-bewertung [projectId]="projectId()" [releaseId]="rid" (decisionSaved)="onDecisionSaved($event)" />
  }
</section>
